// OWNED BY the signatures implementation agent (see ../SignaturesTab.tsx ownership note).
//
// One document row of the customer-page signatures tab (mock s25 `ct-row`),
// with a click-to-expand per-signer detail block porting the essential
// DocumentDetailPanel behaviors (signer statuses + timestamps, OTP badge,
// WhatsApp delivery + failure surfacing, per-signer resend, copy signing
// link, archive, signed/audit/questionnaire downloads).

import { MouseEvent, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography
} from "@material-ui/core";
import {
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  HistoryEdu as HistoryEduIcon,
  MoreVert as MoreVertIcon,
  NotificationsNone as NotificationsNoneIcon,
  OpenInNew as OpenInNewIcon,
  Send as SendIcon,
  WhatsApp as WhatsAppIcon
} from "@material-ui/icons";
import { useSnackbar } from "notistack5";
import Label from "components/Label";
import { buildNewSignatureParams } from "components/_dashboard/signatures/GlobalNewSignatureDialog";
import { displayDocumentStatus, downloadSignedPdfBlob } from "services/signatures";
import { getDocumentFileUrl, getDocumentViewPath } from "services/signPublic";
import { questionnaireDownloadLabel } from "utils/signatureQuestionnaireName";
import { buildSigningLinkWhatsappShare, deliveryRecipientLabel } from "utils/signatureWhatsappShare";
import { sentConfirmation } from "services/signingLinkDelivery";
import WhatsappConfirmDialog from "components/_dashboard/signatures/WhatsappConfirmDialog";
import QuestionnaireStepLabel, {
  SignerQuestionnaireStepLabel
} from "components/_dashboard/signatures/QuestionnaireStepLabel";
import { questionnaireInMessage } from "utils/signatureQuestionnaireStep";
import {
  archiveContactSignatureDocument,
  documentOpenCount,
  documentStatusColor,
  documentStatusLabelFor,
  documentTitleOf,
  isQuestionnaireDocument,
  relativeDayLabel,
  remindableSigners,
  resendSignatureReminder,
  shortDateHe,
  signerProgress,
  signerStatusColor,
  signerStatusLabelHe,
  signersOf,
  wasSentViaWhatsapp
} from "./signaturesData";
import { useContactSurfaceDown } from "../../contactSurface";
import type {
  SignatureDocument,
  SignatureDocumentSigner
} from "../../../../../../@types/signatures";
import type { RealEstateContact } from "../../../../../../@types/sharedSchema";
import ForwardSendIcon from "../../../../../ForwardSendIcon";

type RowGroup = "pending" | "draft" | "completed" | "closed";

const groupOf = (doc: SignatureDocument): RowGroup => {
  switch (doc.status) {
    case "draft":
      return "draft";
    case "completed":
      return "completed";
    case "declined":
    case "expired":
      return "closed";
    default:
      return "pending";
  }
};

const GROUP_ICON_SX: Record<RowGroup, { bgcolor: string; color: string }> = {
  pending: { bgcolor: "warning.lighter", color: "warning.dark" },
  draft: { bgcolor: "grey.200", color: "text.secondary" },
  completed: { bgcolor: "success.lighter", color: "success.dark" },
  closed: { bgcolor: "error.lighter", color: "error.dark" }
};

const formatDateTimeHe = (value?: string): string => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("he-IL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

/** "נשלח היום 10:05 בוואטסאפ · נפתח פעמיים · חתימות 0/1" (mock r2 line). */
const secondaryLineFor = (doc: SignatureDocument): string => {
  const group = groupOf(doc);
  if (group === "draft") {
    const when = doc.updatedTime || doc.createdTime;
    return [when ? `עודכן ${relativeDayLabel(when)}` : "", "טרם נשלח"]
      .filter(Boolean)
      .join(" · ");
  }
  if (group === "completed") {
    const when = doc.completedAt ? ` ${new Date(doc.completedAt).toLocaleDateString("he-IL")}` : "";
    // A questionnaire-only document was filled in as well as signed.
    const done = isQuestionnaireDocument(doc) ? "מולא ונחתם דיגיטלית" : "נחתם דיגיטלית";
    return `${done}${when} · תויק אוטומטית בטאב המסמכים`;
  }
  if (group === "closed") {
    if (doc.status === "expired") return "פג תוקף המסמך";
    const decliner = signersOf(doc).find((s) => s.status === "declined");
    const when = decliner?.declinedAt ? ` ${new Date(decliner.declinedAt).toLocaleDateString("he-IL")}` : "";
    return `נדחה על ידי החותם${when}`;
  }
  const parts: string[] = [];
  if (doc.sentAt) {
    parts.push(`נשלח ${relativeDayLabel(doc.sentAt)}${wasSentViaWhatsapp(doc) ? " בוואטסאפ" : ""}`);
  }
  const opens = documentOpenCount(doc);
  parts.push(
    opens === 0 ? "טרם נפתח" : opens === 1 ? "נפתח פעם אחת" : opens === 2 ? "נפתח פעמיים" : `נפתח ${opens} פעמים`
  );
  const { signed, total } = signerProgress(doc);
  if (total > 0) parts.push(`חתימות ${signed}/${total}`);
  return parts.join(" · ");
};

type Props = {
  contact: RealEstateContact;
  doc: SignatureDocument;
  /**
   * The contracts of this document's SET among the client's documents (`signingSetOf`): a reminder is
   * the set's one message, and its confirmation names the contract that brings the questionnaire.
   */
  setDocuments?: SignatureDocument[];
};

export function SignatureDocRow({ contact, doc, setDocuments }: Props): JSX.Element {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  /**
   * The SURFACE, not the window. This row's whole responsive story is "which of the
   * trailing controls survive" — the 40px kind icon, the status chip's position, and two
   * nowrap action buttons. Asked of the viewport, all four stayed put inside the CRM side
   * panel and the document's own name was squeezed to a couple of characters.
   */
  const isNarrow = useContactSurfaceDown("sm");

  const [expanded, setExpanded] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [reminderMenuAnchor, setReminderMenuAnchor] = useState<HTMLElement | null>(null);
  const [resendingSignerId, setResendingSignerId] = useState<string | null>(null);
  // The signer a reminder waits on the realtor's confirmation for (Trello #231 item 7).
  const [confirmFor, setConfirmFor] = useState<SignatureDocumentSigner | null>(null);
  const [archiving, setArchiving] = useState(false);
  const [downloadingBlob, setDownloadingBlob] = useState(false);

  const group = groupOf(doc);
  const signers = signersOf(doc);
  const remindable = remindableSigners(doc);
  // "שיתוף בוואטסאפ": the realtor's OWN chat with the signer, pre-filled with the link. Sends nothing.
  const whatsappShare =
    group === "pending" && remindable.length > 0 ? buildSigningLinkWhatsappShare([doc], t, remindable[0].id) : null;
  const signerNames = signers.map((s) => s.name).filter(Boolean).join(", ");

  // -- actions -------------------------------------------------------------

  // Every reminder is an automatic send (WhatsApp + SMS from Webot's number): it asks first, every
  // time, from the row's button, the menu, the signer picker and the per-signer "send again" alike.
  const sendReminderTo = (signer: SignatureDocumentSigner): void => setConfirmFor(signer);

  const sendReminderNow = async (signer: SignatureDocumentSigner): Promise<void> => {
    setResendingSignerId(signer.id);
    try {
      const { updated, covered } = await resendSignatureReminder(contact.id, doc, signer.id);
      // The resend endpoint returns HTTP 200 even when the WhatsApp send
      // FAILED — the outcome lives on the signer's whatsappDelivery (BE
      // signatures.service.ts resendWhatsapp). Only celebrate a real 'sent'.
      const updatedSigner = updated.signers?.find((s) => s.id === signer.id);
      if (updatedSigner?.whatsappDelivery?.status === "failed") {
        const reason = updatedSigner.whatsappDelivery.error;
        enqueueSnackbar(
          `שליחת התזכורת ל${signer.name || "חותם"} נכשלה${reason ? `: ${reason}` : ""}`,
          { variant: "error", autoHideDuration: 4000 }
        );
        // The per-signer failure Alert lives in the expanded detail block.
        setExpanded(true);
      } else {
        // To which number, and from where: Webot's number, so not in the realtor's own WhatsApp.
        enqueueSnackbar(sentConfirmation(t, updatedSigner ?? signer, covered), {
          variant: "success",
          autoHideDuration: 6000
        });
      }
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.error || "שליחת התזכורת נכשלה", {
        variant: "error",
        autoHideDuration: 4000
      });
    } finally {
      setResendingSignerId(null);
    }
  };

  const handleReminderClick = (event: MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    if (remindable.length === 1) {
      void sendReminderTo(remindable[0]);
    } else if (remindable.length > 1) {
      setReminderMenuAnchor(event.currentTarget);
    }
  };

  // Reminder from the more-menu (the ONLY reminder entry point on mobile,
  // where the inline button is hidden). Multi-signer documents reuse the
  // more-button as the anchor for the signer picker.
  const handleReminderFromMenu = (): void => {
    const anchor = menuAnchor;
    setMenuAnchor(null);
    if (remindable.length === 1) {
      void sendReminderTo(remindable[0]);
    } else if (remindable.length > 1 && anchor) {
      setReminderMenuAnchor(anchor);
    }
  };

  // "שלח לחתימה" on a draft — opens the existing send flow (the globally-
  // mounted new-signature dialog) with this contact + the draft's template
  // prefilled. URL MERGE pattern is mandatory: ?contact=/?tab= share the URL.
  // NOTE: the send flow CREATES A NEW DOCUMENT from the template (the dialog
  // has no documentId prop — the draft's signers/fieldValues/number are not
  // carried over, and the draft row stays in טיוטות). The tooltip/caption on
  // the affordances below state this so the realtor isn't misled.
  const handleSendDraft = (event: MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    const next = new URLSearchParams(searchParams);
    const params = buildNewSignatureParams({
      contactId: contact.id,
      templateId: doc.templateId
    });
    Object.entries(params).forEach(([k, v]) => next.set(k, v));
    setSearchParams(next);
  };

  const handleCopySigningLink = (): void => {
    const url = (remindable[0] ?? signers[0])?.signing?.publicSigningUrl;
    setMenuAnchor(null);
    if (!url) return;
    navigator.clipboard
      .writeText(url)
      .then(() => enqueueSnackbar("קישור החתימה הועתק", { variant: "success" }))
      .catch(() => enqueueSnackbar("העתקת הקישור נכשלה", { variant: "error" }));
  };

  const handleArchive = async (): Promise<void> => {
    setMenuAnchor(null);
    if (!window.confirm("להעביר את המסמך לארכיון?")) return;
    setArchiving(true);
    try {
      await archiveContactSignatureDocument(contact.id, doc.id);
      enqueueSnackbar("המסמך הועבר לארכיון", { variant: "success" });
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.error || "העברה לארכיון נכשלה", {
        variant: "error",
        autoHideDuration: 4000
      });
    } finally {
      setArchiving(false);
    }
  };

  // Authenticated blob downloads (audit certificate / interim partial PDF) —
  // ported from DocumentDetailPanel: fetched with the token-attaching client,
  // never a plain public href (the audit certificate is realtor-only).
  const handleBlobDownload = async (type: "audit" | "interim"): Promise<void> => {
    setMenuAnchor(null);
    setDownloadingBlob(true);
    try {
      const blob = await downloadSignedPdfBlob(doc.id, type === "audit" ? "audit" : undefined);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${doc.formattedNumber}-${type === "audit" ? "audit" : "partial"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.error || "הורדת הקובץ נכשלה", {
        variant: "error",
        autoHideDuration: 4000
      });
    } finally {
      setDownloadingBlob(false);
    }
  };

  // -- render --------------------------------------------------------------

  const iconSx = GROUP_ICON_SX[group];
  const GroupIcon = group === "draft" ? DescriptionIcon : group === "pending" ? EditIcon : HistoryEduIcon;

  // The status, and the questionnaire's step beside it: a contract signed with its questionnaire still
  // open reads "נחתם, ממתין לשאלון", not only "נחתם חלקית" (the lists' one rule, QuestionnaireStepLabel).
  const statusLabel = (
    <Stack direction="row" alignItems="center" sx={{ gap: 0.5, flexWrap: "wrap" }}>
      <Label color={documentStatusColor[displayDocumentStatus(doc)]} variant="ghost">
        {group === "completed" && doc.completedAt
          ? `✓ נחתם ${shortDateHe(doc.completedAt)}`
          : documentStatusLabelFor(doc)}
      </Label>
      <QuestionnaireStepLabel doc={doc} />
    </Stack>
  );

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        onClick={() => setExpanded((v) => !v)}
        sx={{
          py: 1.5,
          px: 0.5,
          cursor: "pointer",
          borderRadius: 1,
          "&:hover": { bgcolor: "action.hover" }
        }}
      >
        {/* Leading group icon — desktop only. On mobile every horizontal pixel
            belongs to the document name (design feedback 2026-08). */}
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1.5,
            display: isNarrow ? "none" : "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            ...iconSx
          }}
        >
          <GroupIcon sx={{ fontSize: 20 }} />
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle2" noWrap>
            {documentTitleOf(doc)}
            {signerNames && (
              <Typography component="span" variant="body2" color="text.secondary">
                {` — ${signerNames}`}
              </Typography>
            )}
          </Typography>
          {/* Mobile: the status moves to its own SECOND line so the name gets
              the full first line (the trailing chip crowded it out). */}
          <Box sx={{ display: isNarrow ? "block" : "none", mt: 0.25 }}>{statusLabel}</Box>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
            {doc.formattedNumber} · {secondaryLineFor(doc)}
          </Typography>
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          onClick={(e) => e.stopPropagation()}
          sx={{ flexShrink: 0 }}
        >
          <Box sx={{ display: isNarrow ? "none" : "inline-flex" }}>{statusLabel}</Box>

          {group === "pending" && remindable.length > 0 && (
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={handleReminderClick}
              disabled={!!resendingSignerId}
              startIcon={
                resendingSignerId ? (
                  <CircularProgress size={14} />
                ) : (
                  <NotificationsNoneIcon fontSize="small" />
                )
              }
              sx={{ whiteSpace: "nowrap", display: isNarrow ? "none" : "inline-flex" }}
            >
              שלח תזכורת
            </Button>
          )}

          {group === "draft" && (
            <Tooltip title="השליחה תיצור מסמך חדש מהתבנית — הטיוטה תישאר ברשימה">
              <Button
                size="small"
                variant="outlined"
                onClick={handleSendDraft}
                sx={{ whiteSpace: "nowrap", display: isNarrow ? "none" : "inline-flex" }}
              >
                שלח לחתימה
              </Button>
            </Tooltip>
          )}

          {group === "completed" && (
            <>
              <Tooltip title={isQuestionnaireDocument(doc) ? "צפייה בשאלון החתום" : "צפייה במסמך החתום"}>
                <IconButton
                  size="small"
                  component="a"
                  href={getDocumentViewPath(doc.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={isQuestionnaireDocument(doc) ? "הורדת השאלון החתום" : "הורדת המסמך החתום"}>
                <IconButton
                  size="small"
                  component="a"
                  href={getDocumentFileUrl(doc.id, { type: "signed", download: true })}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}

          <IconButton
            size="small"
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            disabled={archiving || downloadingBlob}
            aria-label="עוד פעולות למסמך"
          >
            {archiving || downloadingBlob ? (
              <CircularProgress size={16} />
            ) : (
              <MoreVertIcon fontSize="small" />
            )}
          </IconButton>
        </Stack>
      </Stack>

      {/* Per-signer detail (ported DocumentDetailPanel essentials) */}
      <Collapse in={expanded} unmountOnExit>
        <Stack
          spacing={1.5}
          divider={<Divider />}
          sx={{
            mb: 1.5,
            mx: 0.5,
            p: 1.5,
            borderRadius: 1,
            bgcolor: "background.neutral"
          }}
        >
          {signers.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              אין חותמים במסמך הזה
            </Typography>
          )}
          {signers.map((s) => (
            <Stack key={s.id} spacing={0.75}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" noWrap>
                    {s.name || s.phone}
                  </Typography>
                  {s.phone && (
                    <Typography variant="caption" color="text.secondary" dir="ltr">
                      {s.phone}
                    </Typography>
                  )}
                </Box>
                {s.otpVerifiedAt ? (
                  <Label color="success" variant="ghost">
                    אומת בטלפון
                  </Label>
                ) : doc.otpRequired === false ? (
                  <Label color="warning" variant="ghost">
                    ללא אימות טלפוני
                  </Label>
                ) : null}
                <Label color={signerStatusColor[s.status]} variant="ghost">
                  {signerStatusLabelHe[s.status]}
                </Label>
                <SignerQuestionnaireStepLabel doc={doc} signer={s} />
              </Stack>

              <Typography variant="caption" color="text.secondary">
                {[
                  s.sentAt ? `נשלח: ${formatDateTimeHe(s.sentAt)}` : "",
                  s.viewedAt ? `נצפה: ${formatDateTimeHe(s.viewedAt)}` : "",
                  s.signedAt ? `נחתם: ${formatDateTimeHe(s.signedAt)}` : "",
                  s.declinedAt ? `נדחה: ${formatDateTimeHe(s.declinedAt)}` : ""
                ]
                  .filter(Boolean)
                  .join(" · ") || "טרם נשלח לחותם"}
              </Typography>

              {s.whatsappDelivery?.status === "failed" && (
                <Alert severity="error" sx={{ py: 0.25 }}>
                  שליחת הוואטסאפ נכשלה{s.whatsappDelivery.error ? `: ${s.whatsappDelivery.error}` : ""}
                </Alert>
              )}

              {s.status !== "signed" && s.status !== "declined" && s.signing?.slug && (
                <Box>
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    endIcon={
                      resendingSignerId === s.id ? (
                        <CircularProgress size={14} />
                      ) : (
                        <ForwardSendIcon fontSize="small" />
                      )
                    }
                    disabled={resendingSignerId === s.id}
                    onClick={() => void sendReminderTo(s)}
                  >
                    שליחה חוזרת בוואטסאפ
                  </Button>
                </Box>
              )}
            </Stack>
          ))}
        </Stack>
      </Collapse>

      {/* Reminder target picker (multi-signer documents) */}
      <Menu
        anchorEl={reminderMenuAnchor}
        open={!!reminderMenuAnchor}
        onClose={() => setReminderMenuAnchor(null)}
      >
        {remindable.map((s) => (
          <MenuItem
            key={s.id}
            onClick={() => {
              setReminderMenuAnchor(null);
              void sendReminderTo(s);
            }}
          >
            <ListItemText
              primary={s.name || s.phone}
              secondary={signerStatusLabelHe[s.status]}
              primaryTypographyProps={{ variant: "body2" }}
            />
          </MenuItem>
        ))}
      </Menu>

      {/* Row actions menu */}
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        {group === "pending" && remindable.length > 0 && (
          <MenuItem disabled={!!resendingSignerId} onClick={handleReminderFromMenu}>
            <ListItemIcon>
              <NotificationsNoneIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ variant: "body2" }}>
              שלח תזכורת
            </ListItemText>
          </MenuItem>
        )}
        {whatsappShare && (
          <MenuItem
            component="a"
            href={whatsappShare.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMenuAnchor(null)}
          >
            <ListItemIcon>
              <WhatsAppIcon fontSize="small" sx={{ color: "#25D366" }} />
            </ListItemIcon>
            <ListItemText
              primaryTypographyProps={{ variant: "body2" }}
              secondary={t("signatures.whatsappShare.hint")}
              secondaryTypographyProps={{ variant: "caption" }}
            >
              {t("signatures.whatsappShare.label")}
            </ListItemText>
          </MenuItem>
        )}
        {group === "draft" && (
          <MenuItem
            onClick={(e) => {
              setMenuAnchor(null);
              handleSendDraft(e);
            }}
          >
            <ListItemIcon>
              <SendIcon fontSize="small" sx={{ transform: "scaleX(-1)" }} />
            </ListItemIcon>
            <ListItemText
              primaryTypographyProps={{ variant: "body2" }}
              secondary="השליחה תיצור מסמך חדש מהתבנית"
              secondaryTypographyProps={{ variant: "caption" }}
            >
              שלח לחתימה
            </ListItemText>
          </MenuItem>
        )}
        {(remindable[0] ?? signers[0])?.signing?.publicSigningUrl && (
          <MenuItem onClick={handleCopySigningLink}>
            <ListItemIcon>
              <ContentCopyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ variant: "body2" }}>
              העתקת קישור חתימה
            </ListItemText>
          </MenuItem>
        )}
        {doc.status === "partial" && (
          <MenuItem onClick={() => void handleBlobDownload("interim")}>
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ variant: "body2" }}>
              הורדת המסמך שנחתם עד כה
            </ListItemText>
          </MenuItem>
        )}
        {doc.status === "completed" && doc.signedDocument?.auditLogUrl && (
          <MenuItem onClick={() => void handleBlobDownload("audit")}>
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ variant: "body2" }}>
              הורדת תעודת החתימה האלקטרונית
            </ListItemText>
          </MenuItem>
        )}
        {/* A questionnaire-only document's questionnaire IS its signed PDF, downloaded above. */}
        {doc.status === "completed" && doc.sellerQuestionnaireFile?.url && !isQuestionnaireDocument(doc) && (
          <MenuItem
            component="a"
            href={getDocumentFileUrl(doc.id, { type: "questionnaire", download: true })}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMenuAnchor(null)}
          >
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ variant: "body2" }}>
              {questionnaireDownloadLabel(doc.sellerQuestionnaire?.listedFor)}
            </ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => void handleArchive()} sx={{ color: "error.main" }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ variant: "body2" }}>
            העברה לארכיון
          </ListItemText>
        </MenuItem>
      </Menu>

      <WhatsappConfirmDialog
        open={!!confirmFor}
        signerName={confirmFor?.name}
        signerPhone={confirmFor ? deliveryRecipientLabel({ phone: confirmFor.phone }) : undefined}
        inSet={!!doc.bundleId}
        questionnaire={confirmFor ? questionnaireInMessage(doc, setDocuments) : null}
        onCancel={() => setConfirmFor(null)}
        onConfirm={async () => {
          if (!confirmFor) return;
          await sendReminderNow(confirmFor);
          setConfirmFor(null);
        }}
      />
    </Box>
  );
}
